import { useMemo } from 'react';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatScanDate, scanTakenAt } from '@odo/shared-ui';
import type { RejectedScan, RejectedScanView } from '../../../services/dmRejectedScans';
import { ScanPhotoGrid } from '../actions/dm/scanMarkup/ScanPhotoGrid';
import { markupPhotos } from '../actions/dm/scanMarkup/scanPhotoOptions';
import { RejectedScanHeading } from './RejectedScanCard';
import type { OpenedPhotos } from './RejectedScanPhotoViewer';
import { PHOTOS_UNAVAILABLE, rejectionReasonLine } from './rejectedScanText';

const STEP_BUTTON_CLASS =
    'flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:hover:bg-transparent';

interface ViewPhotosProps {
    view: RejectedScanView;
    title: string;
    onOpenPhotos: (opened: OpenedPhotos) => void;
}

function ViewPhotos({ view, title, onOpenPhotos }: ViewPhotosProps) {
    const photos = useMemo(
        () =>
            markupPhotos(
                view.photos.map(({ key, label, previewUrl, originalUrl }) => ({
                    step: key,
                    label,
                    previewUrl,
                    originalUrl
                }))
            ),
        [view.photos]
    );

    return (
        <section className="flex flex-col gap-2">
            <h4 className="text-xs font-semibold text-gray-700">{view.label}</h4>
            <ScanPhotoGrid
                photos={photos}
                pickVerb="View"
                onPick={(photo) => onOpenPhotos({ title, photos, index: photos.indexOf(photo) })}
            />
        </section>
    );
}

interface ScanPhotoSectionsProps {
    scan: RejectedScan;
    onOpenPhotos: (opened: OpenedPhotos) => void;
}

function ScanPhotoSections({ scan, onOpenPhotos }: ScanPhotoSectionsProps) {
    if (scan.photosUnavailable) {
        return <p className="text-sm text-gray-500">{PHOTOS_UNAVAILABLE}</p>;
    }
    if (scan.views.length === 0) {
        return <p className="text-sm text-gray-500">DM has no photos for this scan</p>;
    }
    const date = formatScanDate(scanTakenAt(scan));
    return (
        <>
            {scan.views.map((view) => (
                <ViewPhotos
                    key={view.view}
                    view={view}
                    title={`${view.label} · ${date}`}
                    onOpenPhotos={onOpenPhotos}
                />
            ))}
        </>
    );
}

interface RejectedScanDetailProps {
    scan: RejectedScan;
    previousScanId: number | null;
    nextScanId: number | null;
    onBack: () => void;
    onOpenScan: (scanId: number) => void;
    onOpenPhotos: (opened: OpenedPhotos) => void;
}

function RejectedScanDetail({
    scan,
    previousScanId,
    nextScanId,
    onBack,
    onOpenScan,
    onOpenPhotos
}: RejectedScanDetailProps) {
    const reasons = rejectionReasonLine(scan.reasons);

    return (
        <div className="flex flex-col gap-4">
            <header className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <button
                        type="button"
                        onClick={onBack}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        All rejected scans
                    </button>
                    <h3>
                        <RejectedScanHeading scan={scan} />
                    </h3>
                    <div className="ml-auto flex gap-2">
                        <button
                            type="button"
                            aria-label="Previous scan"
                            title="Previous scan"
                            disabled={previousScanId == null}
                            onClick={() => previousScanId != null && onOpenScan(previousScanId)}
                            className={STEP_BUTTON_CLASS}
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            aria-label="Next scan"
                            title="Next scan"
                            disabled={nextScanId == null}
                            onClick={() => nextScanId != null && onOpenScan(nextScanId)}
                            className={STEP_BUTTON_CLASS}
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                {reasons && <p className="text-sm text-gray-700">{reasons}</p>}
            </header>
            <ScanPhotoSections scan={scan} onOpenPhotos={onOpenPhotos} />
        </div>
    );
}

export { RejectedScanDetail };
